import React, { useState, useEffect, useCallback } from "react";
import {
  Page,
  Card,
  Text,
  Button,
  Badge,
  TextField,
  Box,
  Popover,
  ActionList,
  Spinner,
  IndexTable,
  Tabs,
  DatePicker,
  Modal,
  Banner,
  Tooltip,
} from "@shopify/polaris";
import { format, parseISO } from "date-fns";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MagicIcon, MenuVerticalIcon } from "@shopify/polaris-icons";
import { Icon } from "@shopify/polaris";
import axios from "axios";
import ArticleModal from "../Modals/ArticleModal";
import DeleteArticleModal from "../Modals/DeleteArticleModal";
import UpdateStatusModal from "../Modals/UpdateStatusModal";
import moment from "moment-timezone";
import GeneratedArticleModal from "../Modals/GeneratedArticleModal";

function AllBlogList({ shopOrigin }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [popoverActive, setPopoverActive] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [articles, setArticles] = useState([]);
  const [pageInfo, setPageInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [prevCursor, setPrevCursor] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingMessage, setProcessingMessage] = useState("");
  const [processingType, setProcessingType] = useState("");
  const [processingArticleId, setProcessingArticleId] = useState(null);
  // const [assistantInstruction, setAssistantInstruction] = useState("");
  const [generatedArticle, setGeneratedArticle] = useState(null);

  const [banner, setBanner] = useState({
    show: false,
    message: "",
    status: "success",
  });
  const [selectedTab, setSelectedTab] = useState(() => {
    // Initialize selectedTab based on query parameter
    const filterParam = searchParams.get("filter");
    if (filterParam === "published") return 1;
    if (filterParam === "unpublished") return 2;
    return 0;
  });
  const [blogCounts, setBlogCounts] = useState({
    total: 0,
    published: 0,
    unpublished: 0,
  });
  const [shopTimezone, setShopTimezone] = useState("UTC");
  const [refreshKey, setRefreshKey] = useState(0);

  const [userPlan, setUserPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(true);


  // DATE PICKER
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [scheduledTime, setScheduledTime] = useState("12:00");
  const [{ month, year }, setDate] = useState({
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  });
  const [selectedDates, setSelectedDates] = useState({
    start: new Date(),
    end: new Date(),
  });

  // Handle banner auto-dismiss
  useEffect(() => {
    if (banner.show) {
      const timeoutId = setTimeout(() => {
        setBanner({ show: false, message: "", status: "success" });
      }, 3000);
      return () => clearTimeout(timeoutId);
    }
  }, [banner.show]);

  const isDateInFuture = (date, timezone) => {
    if (!date) return false;
    const now = moment.tz(timezone);
    const targetDate = moment.tz(date, timezone);
    return targetDate.isAfter(now);
  };


  const fetchUserSubscription = useCallback(async () => {
    if (!shopOrigin) return;
    setPlanLoading(true);
    try {
      const response = await axios.get(`/get_subscription/?shop_name=${shopOrigin}`);
      setUserPlan(response.data.plan); // Will be 'free', 'basic', 'pro', or null
    } catch (error) {
      console.error("Error fetching subscription:", error);
      setUserPlan(null);
    } finally {
      setPlanLoading(false);
    }
  }, [shopOrigin]);

  useEffect(() => {
    if (!shopOrigin) return;
    fetchUserSubscription();
  }, [shopOrigin, fetchUserSubscription]);


  const tabs = [
    { id: "all", content: `All (${blogCounts.total})` },
    { id: "published", content: `Published (${blogCounts.published})` },
    { id: "unpublished", content: `Unpublished (${blogCounts.unpublished})` },
  ];

  // Fetch blog counts
  const fetchBlogCounts = useCallback(async () => {
    try {
      const response = await axios.get(
        `/api/get-articles-count?shop=${shopOrigin}`
      );
      const { total_articles, published_articles, unpublished_articles } =
        response.data;
      setBlogCounts({
        total: total_articles,
        published: published_articles,
        unpublished: unpublished_articles,
      });
    } catch (error) {
      console.error("Error fetching blog counts:", error);
    }
  }, [shopOrigin]);

  // Fetch articles
  const fetchArticles = useCallback(
    async ({ next = null, prev = null, filter = "" } = {}) => {
      setLoading(true);
      try {
        let queryFilter = filter;
        if (selectedTab === 1) {
          queryFilter += `${
            queryFilter ? " AND " : ""
          }published_status:published`;
        } else if (selectedTab === 2) {
          queryFilter += `${
            queryFilter ? " AND " : ""
          }published_status:unpublished`;
        }

        let url = `/api/blog-pagination-api/?shop=${shopOrigin}`;
        if (next) url += `&nextCursor=${encodeURIComponent(next)}`;
        if (prev) url += `&prevCursor=${encodeURIComponent(prev)}`;
        if (queryFilter) url += `&filter=${encodeURIComponent(queryFilter)}`;

        const response = await axios.get(url);
        const allArticles = response.data.articles || [];

        setArticles(
          allArticles.map((article) => ({
            ...article,
            status: article.published
              ? article.published_at &&
                isDateInFuture(
                  moment.tz(article.published_at, shopTimezone),
                  shopTimezone
                )
                ? "Scheduled"
                : "Published"
              : "Unpublished",
          }))
        );

        setPageInfo(response.data.pageInfo || {});
        setNextCursor(response.data.pageInfo?.endCursor || null);
        setPrevCursor(response.data.pageInfo?.startCursor || null);
      } catch (error) {
        console.error("Error fetching articles:", error);
        setArticles([]);
        setPageInfo({});
      } finally {
        setLoading(false);
      }
    },
    [shopOrigin, selectedTab, shopTimezone]
  );

  const simulateProgress = (targetProgress = 98, duration = 30000) => {
    const startTime = Date.now();
    const updateInterval = 100; // Update every 100ms for smooth animation

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progressRatio = Math.min(elapsed / duration, 1);

      // Use easing function for smooth deceleration
      const easedProgress = 1 - Math.pow(1 - progressRatio, 3); // Cubic ease-out
      const currentProgress = Math.floor(easedProgress * targetProgress);

      setProcessingProgress((prev) => {
        if (prev >= targetProgress) {
          clearInterval(interval);
          return prev;
        }
        return currentProgress;
      });
    }, updateInterval);

    return interval;
  };

  const handleOptimizeArticle = async (item) => {
    let progressInterval;

    try {
      setIsProcessing(true);
      setProcessingProgress(0);
      setProcessingMessage("Optimizing your article...");
      setProcessingType("optimize");
      setProcessingArticleId(item.article_id);

      progressInterval = simulateProgress(98, 30000);

      // FIX: Safely extract article content and title from item
      // Based on your fetch response structure
      const originalHtml = item.body || item.article_body || item.content || "";
      const originalTitle = item.title || item.article_title || "";

      // Validate that we have the required content
      if (!originalHtml || !originalTitle) {
        throw new Error(
          "Article content or title is missing. Cannot optimize."
        );
      }

      // --- Step 1: Optimize the article ---
      console.log("Step 1: Sending article for optimization...");
      const optimizeResponse = await axios.post(
        `/api/optimize-article/${encodeURIComponent(shopOrigin || "")}/`,
        {
          article_id: item.article_id,
          title: originalTitle,
          content: originalHtml,
          language: item.language || "English",
          tone: item.tone || undefined,
        },
        { headers: { "Content-Type": "application/json" } }
      );
      console.log(
        "Step 1 Complete: Optimization response received.",
        optimizeResponse.data
      );

      // Stop the progress simulation after the API call completes
      clearInterval(progressInterval);
      setProcessingProgress(100);

      if (
        optimizeResponse.data.status === "success" &&
        optimizeResponse.data.article
      ) {
        const optimizedArticle = optimizeResponse.data.article;

        // --- Step 2: Save the optimized content back to Shopify ---
        console.log("Step 2: Publishing optimized article to Shopify...");
        const publishResponse = await axios.post(
          `/publish-article/${shopOrigin}/`,
          {
            article_id: item.article_id,
            article_title: optimizedArticle.article_title,
            article_content: optimizedArticle.article_body,
            status: item.status, // Keep the current publish status
            blogid: item.blog_id || item.blogid, // Handle both possible property names
          }
        );
        console.log(
          "Step 2 Complete: Publish response received.",
          publishResponse.data
        );

        if (publishResponse.data.status === "success") {
          // --- Step 3: Navigate to the edit page on complete success ---
          console.log(
            "Step 3: All steps successful. Navigating to edit page..."
          );
          navigate(
            `/edit-blogs?shop=${encodeURIComponent(
              shopOrigin
            )}&article_id=${encodeURIComponent(item.article_id)}`
          );
        } else {
          throw new Error(
            publishResponse.data.message ||
              "Failed to save the optimized article to Shopify."
          );
        }
      } else {
        throw new Error(
          optimizeResponse.data.message ||
            "Optimization failed to return valid content."
        );
      }
    } catch (err) {
      // Clear interval on error
      if (progressInterval) clearInterval(progressInterval);

      console.error("Optimization process failed:", err.message);
      setBanner({
        show: true,
        message:
          err.message || "An unexpected error occurred during optimization.",
        status: "critical",
      });

      // Reset processing state after showing the error
      setTimeout(() => {
        setIsProcessing(false);
        setProcessingArticleId(null);
        setProcessingType(null);
        setProcessingProgress(0);
      }, 500);
    }
  };

  const handleGenerateSimilar = async (item) => {
    let progressInterval;

    try {
      setIsProcessing(true);
      setProcessingProgress(0);
      setProcessingMessage("Generating similar article...");
      setProcessingType("generate");
      setProcessingArticleId(item.article_id);

      // FIX: Safely extract title from item
      // Handle multiple possible property names
      const baseTitle = item.title || item.article_title || "";

      // Validate that we have a title
      if (!baseTitle) {
        throw new Error(
          "Article title is missing. Cannot generate similar article."
        );
      }

      // Start smooth progress from 0 to 98% over 30 seconds
      progressInterval = simulateProgress(98, 30000);

      const response = await axios.post(
        `/api/generate-similar-article/?shop=${shopOrigin}`,
        {
          base_title: baseTitle, // Use the extracted title
          language: item.language || "English", // Direct property access (not item.raw.language)
          tone: item.tone || "professional", // Direct property access (not item.raw.tone)
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      clearInterval(progressInterval);
      setProcessingProgress(100);

      if (response.data.status === "success" && response.data.article) {
        const newArticle = {
          ...response.data.article,
          published: false,
          created_at: new Date().toISOString(), // Changed from createdat
          updated_at: new Date().toISOString(), // Changed from updatedat
        };

        setGeneratedArticle(newArticle);
        setModalOpen(true);
      } else {
        throw new Error(response.data.message || "Generation failed");
      }
    } catch (error) {
      // Clear interval on error
      if (progressInterval) clearInterval(progressInterval);

      console.error("Error generating similar article:", error.message);
      setBanner({
        show: true,
        message: error.message || "Failed to generate similar article",
        status: "critical",
      });
    } finally {
      setTimeout(() => {
        setIsProcessing(false);
        setProcessingArticleId(null);
        setProcessingType(null);
        setProcessingProgress(0);
      }, 500);
    }
  };

  // Fetch shop timezone
  const fetchShopTimezone = useCallback(async () => {
    try {
      const response = await axios.get(
        `/api/get-shop-timezone/?shop=${shopOrigin}`
      );
      if (response.data.status === "success" && response.data.timezone) {
        setShopTimezone(response.data.timezone);
      } else {
        setShopTimezone("UTC");
      }
    } catch (error) {
      console.error("Error fetching shop timezone:", error);
      setShopTimezone("UTC");
    }
  }, [shopOrigin]);

  // Initialize data
  useEffect(() => {
    if (!shopOrigin) return;

    const initialize = async () => {
      setLoading(true);
      try {
        // First fetch timezone, then other data
        await fetchShopTimezone();
        await fetchBlogCounts();
        await fetchArticles({ filter: searchValue });
      } catch (error) {
        console.error("Error initializing data:", error);
        setBanner({
          show: true,
          message: "Failed to load data",
          status: "critical",
        });
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [shopOrigin, searchValue, refreshKey, selectedTab]);

  const togglePopover = (index) => {
    if (selectedRowIndex === index && popoverActive) {
      setPopoverActive(false);
      setSelectedRowIndex(null);
    } else {
      setPopoverActive(true);
      setSelectedRowIndex(index);
    }
  };

  const activator = (index) => (
    <span onClick={() => togglePopover(index)} style={{ cursor: "pointer" }}>
      <Icon source={MenuVerticalIcon} tone="base" />
    </span>
  );

  const handleSearchChange = (value) => {
    setSearchValue(value);
  };

  const handleDeleteSuccess = () => {
    setBanner({
      show: true,
      message: "Article deleted",
      status: "success",
    });
    setRefreshKey((prev) => prev + 1);
  };

  const handleStatusUpdateSuccess = () => {
    setBanner({
      show: true,
      message: "Status updated",
      status: "success",
    });
    setRefreshKey((prev) => prev + 1);
  };

  const handleSetVisibilityDate = async () => {
    try {
      const [hours, minutes] = scheduledTime.split(":");
      const updatedDate = new Date(selectedDates.start);

      // console.log("UpdatedDate set on datePicker::", updatedDate);

      if (isNaN(updatedDate.getTime())) {
        console.error("Invalid date selected");
        setBanner({
          show: true,
          message: "Invalid date selected",
          status: "critical",
        });
        return;
      }

      updatedDate.setHours(parseInt(hours), parseInt(minutes));

      // Determine if the date is in the future - SAME AS DASHBOARD
      const isFuture = isDateInFuture(updatedDate, shopTimezone);
      // console.log("isFuture: ", isFuture);

      // Update article via API
      const response = await axios.post(
        `/api/update-article-schedule/?shop=${shopOrigin}`,
        {
          shop: shopOrigin,
          article_id: selectedArticle?.article_id,
          published_at: updatedDate,
          published: !isFuture,
        }
      );

      const data = {
        shop: shopOrigin,
        article_id: selectedArticle?.article_id,
        published_at: updatedDate,
        published: !isFuture,
      };
      // console.log("data send to backend for update schedule: ", data);

      if (response.data.status === "success") {
        setBanner({
          show: true,
          message: `Article scheduled for ${moment(updatedDate).format(
            "ddd MMM DD YYYY HH:mm"
          )}`,
          status: "success",
        });
        setIsDateModalOpen(false);
        setRefreshKey((prev) => prev + 1); // Refresh the article list
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error("Error scheduling article:", error);
      setBanner({
        show: true,
        message: "Failed to schedule article",
        status: "critical",
      });
    }
  };

  const navigateToneSetup = () => {
    navigate(`/tone-content?shop=${encodeURIComponent(shopOrigin || "")}`);
  };

  const rowMarkup = articles.map((item, index) => (
    <IndexTable.Row id={item.article_id} key={item.article_id} position={index}>
      <IndexTable.Cell>
        <div>
          <Text fontWeight="medium" as="span">
            {item.article_title}
          </Text>
          <div style={{ color: "#6d7175", fontSize: "13px" }}>
            {item.author || "Anonymous"}
          </div>
        </div>
      </IndexTable.Cell>
      <IndexTable.Cell>
        {item.keywords?.length > 0
          ? item.keywords.map((keyword) => (
              <Badge key={keyword}>{keyword}</Badge>
            ))
          : "-"}
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text>{item.blog_title}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        {item.updated_at
          ? format(parseISO(item.updated_at), "dd/MM/yyyy, HH:mm:ss")
          : "-"}
      </IndexTable.Cell>
      <IndexTable.Cell>
        {item.status === "Published" ? (
          <Badge tone="success">Published</Badge>
        ) : item.status === "Scheduled" ? (
          <Badge tone="info">Scheduled</Badge>
        ) : (
          <Badge tone="info">Draft</Badge>
        )}
      </IndexTable.Cell>
      <IndexTable.Cell>
        <div
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            flexDirection: "row",
            whiteSpace: "nowrap",
          }}
        >
          {/* Optimize Button with fixed width */}
          <div style={{ minWidth: '120px', position: 'relative' }}>
            {userPlan === "free" ? (
              <Tooltip content="Please Upgrade Plan" dismissOnMouseOut>
                <div>
                  <Button
                    variant="primary"
                    disabled={true}
                    fullWidth
                  >
                    Optimize
                  </Button>
                </div>
              </Tooltip>
            ) : (
            <Button
              variant="primary"
              disabled={isProcessing}
              onClick={() => handleOptimizeArticle(item)}
              fullWidth
            >
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  gap: '6px' 
                }}>
                {processingArticleId === item.article_id &&
                isProcessing &&
                    processingType === "optimize"
                      ? (
                        <div style={{display:"flex", alignItems:"center", justifyContent:"center", gap:"10px"}}>
                    <div>
                            <p style={{fontSize:"12px"}} className="processing-submessage">
                              {processingProgress}% {" "}
                      </p>
                    </div>
                    <div>
                            <Spinner accessibilityLabel="Loading spinner" size="small" />
                    </div>
                  </div>
                      )
                      : "Optimize"}
              </div>
            </Button>
            )}
          </div>

          {/* Generate Similar Button with fixed width */}
          <div style={{ minWidth: '150px', position: 'relative' }}>
            {userPlan === "free" ? (
              <Tooltip content="Please Upgrade Plan" dismissOnMouseOut>
                <div>
                  <Button
                    disabled={true}
                    fullWidth
                  >
                    Generate Similar
                  </Button>
                </div>
              </Tooltip>
            ) : (
            <Button
              disabled={isProcessing}
              onClick={() => handleGenerateSimilar(item)}
              fullWidth
            >
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  gap: '6px'
                }}>
                {processingArticleId === item.article_id &&
                isProcessing &&
                    processingType === "generate"
                      ? (
                        <div style={{display:"flex", alignItems:"center", justifyContent:"center", gap:"10px"}}>
                    <div>
                            <p style={{fontSize:"12px"}} className="processing-submessage">
                              {processingProgress}% {" "}
                      </p>
                    </div>
                          <div><Spinner accessibilityLabel="Loading spinner" size="small" /></div>
                    </div>
                      )
                      : "Generate Similar"}
                  </div>
              </Button>
              )}
          </div>
        </div>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Popover
          active={selectedRowIndex === index && popoverActive}
          activator={
            <span
              onClick={() => togglePopover(index)}
              style={{ cursor: "pointer" }}
            >
              <Icon source={MenuVerticalIcon} tone="base" />
            </span>
          }
          onClose={() => {
            setPopoverActive(false);
            setSelectedRowIndex(null);
          }}
        >
          <ActionList
            items={[
              {
                content: "View",
                onAction: () => {
                  setSelectedArticle(item);
                  setModalOpen(true);
                  setPopoverActive(false);
                },
              },
              {
                content: "Edit",
                onAction: () => {
                  navigate(
                    `/edit-blogs?shop=${encodeURIComponent(
                      shopOrigin
                    )}&article_id=${encodeURIComponent(item.article_id)}`
                  );
                  setPopoverActive(false);
                },
              },
              {
                content: "Schedule",
                onAction: () => {
                  if (item.published_at) {
                    // Parse the existing published_at into shop timezone - SAME AS DASHBOARD
                    const scheduledDate = moment
                      .tz(item.published_at, shopTimezone)
                      .toDate();

                    setSelectedDates({
                      start: scheduledDate,
                      end: scheduledDate,
                    });

                    setScheduledTime(
                      moment(scheduledDate).format("HH:mm") // Pre-fill time in 24h format
                    );

                    setDate({
                      month: scheduledDate.getMonth(),
                      year: scheduledDate.getFullYear(),
                    });
                  } else {
                    // Default to today - SAME AS DASHBOARD
                    const today = new Date();
                    setSelectedDates({ start: today, end: today });
                    setScheduledTime("12:00"); // Default time
                    setDate({
                      month: today.getMonth(),
                      year: today.getFullYear(),
                    });
                  }

                  setSelectedArticle(item);
                  setIsDateModalOpen(true);
                  setPopoverActive(false);
                },
              },
              {
                content: item.published ? "Set as Draft" : "Publish",
                onAction: () => {
                  setSelectedArticle(item);
                  setStatusModalOpen(true);
                  setPopoverActive(false);
                },
              },
              {
                content: "Delete",
                destructive: true,
                onAction: () => {
                  setSelectedArticle(item);
                  setDeleteModalOpen(true);
                  setPopoverActive(false);
                },
              },
            ]}
          />
        </Popover>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  const customStyles = `
    .custom-table thead tr {
      background-color: #f4f6f8;
      border-radius:0px;
    }
    .Polaris-Spinner--sizeSmall svg {
      height: 1.1rem;
      width: 1.1em;
    }
    @media only screen and (max-width: 47.9975em) {
      .Polaris-Frame__Content {
        transform: translate(0px, 0px) !important;
      }
    }
    td:first-child{ width: 40%; }
    td:nth-child(3){ width: 15%; }
    @media(max-width: 1600px){
      .Polaris-IndexTable__TableCell { white-space: normal; }
    }
  `;

  return (
    <div style={{ marginBottom: "15px" }}>
      <Page fullWidth>
        <style>{customStyles}</style>

        <div style={{ margin: "10px" }}>
          {banner.show && (
            <Banner
              title={banner.message}
              status={banner.status}
              onDismiss={() =>
                setBanner({ show: false, message: "", status: "success" })
              }
            />
          )}
        </div>

        {loading && (
          <div
            style={{
              position: "fixed", // Fixed positioning to stay in place
              top: 0, // Top edge of the viewport
              left: 0, // Left edge of the viewport
              width: "100vw", // Full viewport width
              height: "100vh", // Full viewport height
              background: "rgba(255,255,255,0.7)", // Semi-transparent background
              zIndex: 9999, // Make sure the spinner is on top
              display: "flex", // Use flexbox to center the spinner
              justifyContent: "center", // Center horizontally
              alignItems: "center", // Center vertically
            }}
          >
            <Spinner accessibilityLabel="Loading articles" size="large" />
          </div>
        )}

        <div
          className="index_header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginLeft: "10px",
            marginRight: "10px",
          }}
        >
          <Text variant="headingLg" as="h5">
            My Posts
          </Text>
          <Button
            onClick={navigateToneSetup}
            variant="primary"
            icon={MagicIcon}
            size="medium"
          >
            Create content with AI
          </Button>
        </div>

        <div style={{ marginTop: "20px" }}>
          <Card padding="0">
            <div style={{ padding: "12px" }}>
              <TextField
                placeholder="Search by title or keyword"
                value={searchValue}
                onChange={handleSearchChange}
                autoComplete="off"
              />
            </div>

            <div>
              <Tabs
                tabs={tabs}
                selected={selectedTab}
                onSelect={(index) => {
                  setSelectedTab(index);
                  // Update URL to reflect tab selection
                  const filter =
                    index === 1
                      ? "published"
                      : index === 2
                      ? "unpublished"
                      : "";
                  navigate(
                    `/all-blogs-listing?shop=${encodeURIComponent(shopOrigin)}${
                      filter ? `&filter=${filter}` : ""
                    }${
                      searchValue
                        ? `&search=${encodeURIComponent(searchValue)}`
                        : ""
                    }`
                  );
                }}
              />
            </div>

            <IndexTable
              selectable={false}
              resourceName={{ singular: "article", plural: "articles" }}
              itemCount={articles.length}
              headings={[
                { title: "Title" },
                { title: "Keywords" },
                { title: "Blog" },
                { title: "Last Modified" },
                { title: "Status" },
                { title: "Actions" },
                { title: "" },
              ]}
            >
              {rowMarkup}
            </IndexTable>

            <Box
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "16px",
                padding: "0 0 20px 0",
              }}
            >
              <Button
                disabled={!pageInfo.hasPreviousPage}
                onClick={() =>
                  fetchArticles({ prev: prevCursor, filter: searchValue })
                }
              >
                Previous
              </Button>
              <Button
                disabled={!pageInfo.hasNextPage}
                onClick={() =>
                  fetchArticles({ next: nextCursor, filter: searchValue })
                }
              >
                Next
              </Button>
            </Box>
          </Card>
        </div>

        <ArticleModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          article={selectedArticle}
        />
        <DeleteArticleModal
          open={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          article={selectedArticle}
          shopOrigin={shopOrigin}
          onDeleteSuccess={handleDeleteSuccess}
        />
        <UpdateStatusModal
          open={statusModalOpen}
          onClose={() => setStatusModalOpen(false)}
          article={selectedArticle}
          shopOrigin={shopOrigin}
          onStatusUpdateSuccess={handleStatusUpdateSuccess}
        />
        <Modal
          open={isDateModalOpen}
          onClose={() => setIsDateModalOpen(false)}
          title="Set visibility date"
          primaryAction={{
            content: "Set visibility date",
            onAction: handleSetVisibilityDate,
          }}
          secondaryActions={[
            { content: "Cancel", onAction: () => setIsDateModalOpen(false) },
          ]}
        >
          <Modal.Section>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              <div
                style={{
                  textAlign: "center",
                  fontWeight: "bold",
                  fontSize: "16px",
                }}
              >
                {new Date(year, month).toLocaleString("en-US", {
                  month: "long",
                })}
              </div>
              <DatePicker
                month={month}
                year={year}
                onChange={setSelectedDates}
                onMonthChange={(m, y) => setDate({ month: m, year: y })}
                selected={selectedDates}
              />
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <div style={{ width: "100%" }}>
                  <TextField
                    label="Time"
                    type="time"
                    value={scheduledTime}
                    onChange={setScheduledTime}
                  />
                </div>
              </div>
            </div>
          </Modal.Section>
        </Modal>
        <GeneratedArticleModal
          open={modalOpen && generatedArticle !== null}
          onClose={() => {
            setModalOpen(false);
            setSelectedArticle(null);
            setGeneratedArticle(null);
          }}
          article={generatedArticle}
          shopOrigin={shopOrigin}
        />
      </Page>
    </div>
  );
}

export default AllBlogList;
